import { useEffect } from "react";

const TABLE_SELECTOR = ".main-content table.admin-list-table";
const CONTAINER_SELECTOR = ".admin-list-table-container";

const copyColumnWidths = (sourceHead: HTMLTableSectionElement, clonedHead: HTMLTableSectionElement) => {
  const sourceCells = Array.from(sourceHead.querySelectorAll<HTMLTableCellElement>("th"));
  const clonedCells = Array.from(clonedHead.querySelectorAll<HTMLTableCellElement>("th"));

  clonedCells.forEach((cell, index) => {
    const sourceCell = sourceCells[index];
    if (!sourceCell) return;

    const width = sourceCell.getBoundingClientRect().width;
    cell.style.width = `${width}px`;
    cell.style.minWidth = `${width}px`;
    cell.style.maxWidth = `${width}px`;
    const sourceStyle = window.getComputedStyle(sourceCell);
    cell.style.padding = sourceStyle.padding;
    cell.style.textAlign = sourceStyle.textAlign;
    // The overlay spans the viewport, while its table spans all columns.
    // Keep a right-pinned source header aligned with its visible action cells.
    if (sourceStyle.position === "sticky" && sourceStyle.right !== "auto") {
      cell.style.position = "relative";
      cell.style.right = "auto";
      cell.style.transform = "";
      cell.style.zIndex = sourceStyle.zIndex;
      cell.style.backgroundColor = sourceStyle.backgroundColor;
      cell.style.transform = `translateX(${sourceCell.getBoundingClientRect().left - cell.getBoundingClientRect().left}px)`;
    }
  });
};

const createHeaderClone = (table: HTMLTableElement) => {
  const sourceHead = table.tHead;
  if (!sourceHead) return null;

  const overlay = document.createElement("div");
  overlay.className = "admin-sticky-table-header";
  overlay.setAttribute("aria-hidden", "true");

  const clonedTable = table.cloneNode(false) as HTMLTableElement;
  clonedTable.removeAttribute("id");
  clonedTable.style.position = "absolute";
  clonedTable.style.left = "0";
  clonedTable.style.top = "0";
  clonedTable.style.margin = "0";

  const clonedHead = sourceHead.cloneNode(true) as HTMLTableSectionElement;
  clonedTable.appendChild(clonedHead);
  overlay.appendChild(clonedTable);
  document.body.appendChild(overlay);

  return { overlay, clonedTable, clonedHead };
};

const StickyTableHeaders: React.FC = () => {
  useEffect(() => {
    const mainContent = document.querySelector<HTMLElement>(".main-content");
    if (!mainContent) return undefined;

    const clones = new Map<HTMLTableElement, ReturnType<typeof createHeaderClone>>();
    const scrollContainers = new Set<HTMLElement>();
    let animationFrame: number | null = null;

    const syncHeaders = () => {
      animationFrame = null;
      const mainRect = mainContent.getBoundingClientRect();
      const tables = new Set(
        Array.from(document.querySelectorAll<HTMLTableElement>(TABLE_SELECTOR)).filter(
          (table) => !table.closest(".admin-list-table-frame")
        )
      );

      tables.forEach((table) => {
        const sourceHead = table.tHead;
        const container = table.closest<HTMLElement>(CONTAINER_SELECTOR);
        if (!sourceHead || !container) return;

        if (!scrollContainers.has(container)) {
          container.addEventListener("scroll", scheduleSync, { passive: true });
          scrollContainers.add(container);
        }

        let clone = clones.get(table);
        if (!clone) {
          clone = createHeaderClone(table);
          if (clone) clones.set(table, clone);
        }
        if (!clone) return;

        const tableRect = table.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const headRect = sourceHead.getBoundingClientRect();
        const visibleLeft = Math.max(mainRect.left, containerRect.left);
        const visibleRight = Math.min(mainRect.right, containerRect.right);
        const visibleWidth = visibleRight - visibleLeft;
        const shouldShow =
          headRect.height > 0 &&
          tableRect.top < mainRect.top &&
          tableRect.bottom > mainRect.top &&
          visibleWidth > 0 &&
          mainRect.bottom > mainRect.top;

        if (!shouldShow) {
          clone.overlay.style.display = "none";
          return;
        }

        clone.overlay.style.display = "block";
        clone.overlay.style.left = `${visibleLeft}px`;
        clone.overlay.style.top = `${mainRect.top}px`;
        clone.overlay.style.width = `${visibleWidth}px`;
        clone.overlay.style.height = `${headRect.height}px`;

        clone.clonedTable.style.width = `${tableRect.width}px`;
        clone.clonedTable.style.transform = `translateX(${tableRect.left - visibleLeft}px)`;
        copyColumnWidths(sourceHead, clone.clonedHead);
      });

      clones.forEach((clone, table) => {
        if (!tables.has(table)) {
          clone?.overlay.remove();
          clones.delete(table);
        }
      });

      scrollContainers.forEach((container) => {
        const stillUsed = Array.from(tables).some(
          (table) => table.closest<HTMLElement>(CONTAINER_SELECTOR) === container
        );
        if (!stillUsed) {
          container.removeEventListener("scroll", scheduleSync);
          scrollContainers.delete(container);
        }
      });
    };

    const scheduleSync = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(syncHeaders);
    };

    mainContent.addEventListener("scroll", scheduleSync, { passive: true });
    document.addEventListener("scroll", scheduleSync, { capture: true, passive: true });
    window.addEventListener("resize", scheduleSync);
    const mutationObserver = new MutationObserver(scheduleSync);
    mutationObserver.observe(mainContent, { childList: true, subtree: true });
    scheduleSync();

    return () => {
      mainContent.removeEventListener("scroll", scheduleSync);
      document.removeEventListener("scroll", scheduleSync, { capture: true });
      window.removeEventListener("resize", scheduleSync);
      mutationObserver.disconnect();
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      scrollContainers.forEach((container) => container.removeEventListener("scroll", scheduleSync));
      clones.forEach((clone) => clone?.overlay.remove());
    };
  }, []);

  return null;
};

export default StickyTableHeaders;
